import type { DesignarJuradoInput, RolJurado } from "@copi/shared";

import { apiClient } from "./client";
import type { ExpedienteWire } from "./expedientes";
import { unwrap } from "./unwrap";

export interface JuradoEvaluadorWire {
  id: string;
  expedienteId: string;
  resolucionId: string;
  fechaDesignacion: string;
  miembros: Array<{
    id: string;
    docenteId: string;
    rolJurado: RolJurado;
    esTitular: boolean;
    estado: "ACTIVO" | "REEMPLAZADO";
  }>;
}

/**
 * Shape devuelto por `GET /jurado/mis-asignaciones`. ADR 028 — un único
 * jurado por expediente; el actor docente puede ser miembro activo de
 * varios expedientes en distintos estados.
 */
export interface AsignacionJuradoWire {
  miembroJuradoId: string;
  rolJurado: RolJurado;
  esTitular: boolean;
  fechaAlta: string;
  expediente: ExpedienteWire;
}

/**
 * Shape devuelto por `GET /expedientes/:id/jurados` (ADR 028 — un único
 * jurado por expediente). El endpoint devuelve un array para preservar
 * la forma del wire (cero o una fila); en condiciones normales solo
 * trae uno.
 */
export interface JuradoConMiembrosWire {
  id: string;
  resolucionId: string | null;
  fechaDesignacion: string;
  estado: string;
  miembros: Array<{
    id: string;
    docenteId: string;
    rolJurado: RolJurado;
    esTitular: boolean;
    estado: "ACTIVO" | "REEMPLAZADO";
    docente: {
      nombres: string;
      apellidos: string;
      codigoDocente: string | null;
    };
  }>;
}

/** Wire format: `Date` viaja como ISO string; backend la reconstruye con `z.coerce.date()`. */
type DesignarJuradoWire = Omit<DesignarJuradoInput, "fechaResolucion"> & {
  fechaResolucion: string;
};

export const juradoApi = {
  designar(expedienteId: string, input: DesignarJuradoInput): Promise<JuradoEvaluadorWire> {
    const wire: DesignarJuradoWire = {
      miembros: input.miembros,
      numeroResolucion: input.numeroResolucion,
      fechaResolucion: input.fechaResolucion.toISOString(),
    };
    return apiClient
      .post(`/expedientes/${expedienteId}/jurado`, wire, { idempotent: true })
      .then(unwrap<JuradoEvaluadorWire>);
  },

  /** Asignaciones activas del docente autenticado en algún jurado. */
  misAsignaciones(): Promise<AsignacionJuradoWire[]> {
    return apiClient.get("/jurado/mis-asignaciones").then(unwrap<AsignacionJuradoWire[]>);
  },

  /**
   * Lista los jurados del expediente (cero o uno; el endpoint devuelve
   * array para mantener consistencia futura). Autorizado por el backend
   * según rol + ownership.
   */
  listarPorExpediente(expedienteId: string): Promise<JuradoConMiembrosWire[]> {
    return apiClient
      .get(`/expedientes/${expedienteId}/jurados`)
      .then(unwrap<JuradoConMiembrosWire[]>);
  },
};

/**
 * Query keys del namespace de jurados. Convención: prefijo
 * `["expedientes", expedienteId, "jurados"]` para que el invalidador
 * de SSE (ADR 018) pueda invalidar por prefix sin conocer el resto.
 */
export const juradoQueryKeys = {
  listarPorExpediente: (expedienteId: string) => ["expedientes", expedienteId, "jurados"] as const,
};
